<script setup lang="ts">
import { useI18n } from "vue-i18n";

import { getDifference } from "@/common/utils/dates";
import type { EvaluationListType } from "@/common/types/evaluation";

import { baseAvatar, svgIcon } from "@/common/components";

const { t } = useI18n();

withDefaults(
  defineProps<{
    evaluation: EvaluationListType;
    avatarSize?: string;
    active?: boolean;
    showProgress?: boolean;
    showArrow?: boolean;
  }>(),
  {
    avatarSize: "5rem",
    showProgress: true,
    showArrow: false,
  }
);

const statusList: { [key: string]: string } = {
  PENDING: "time",
  STARTED: "time-b",
  WARNING: "info",
  CANCELED: "close",
  FINISHED: "confirmed",
};

const statusColor: { [key: string]: string } = {
  PENDING: "#666",
  STARTED: "#666",
  WARNING: "red",
  CANCELED: "orange",
  FINISHED: "green",
};
</script>

<template>
  <div
    class="card-evaluation"
    :class="{ active: active, 'no-progress': !showProgress }"
  >
    <div class="header">
      <base-avatar
        :url="evaluation?.company?.avatar?.small"
        :size="avatarSize"
      />

      <div class="infos">
        <div class="content">
          <div class="code">
            <span>
              {{ evaluation.code }}
            </span>
            <span :style="{ color: statusColor[evaluation.status] }">
              <svg-icon size="1.35rem" :icon="statusList[evaluation.status]" />
            </span>
          </div>

          <template v-if="showArrow">
            <div class="arrow">
              <svg-icon
                :icon="active ? 'arrow-right' : 'arrow-down'"
                size="1rem"
              />
            </div>
          </template>
        </div>

        <div class="company ellipsis">
          {{ evaluation.company?.name }}
        </div>
      </div>
    </div>

    <template v-if="showProgress">
      <div class="progress">
        <div class="item">
          <div class="title">
            {{ t("common.labels.total_duration") }}
          </div>
          <div class="time">
            <svg-icon icon="time-b" size="2rem" />

            <template v-if="!evaluation.start_date">
              <span>0m</span>
            </template>

            <template v-if="evaluation.start_date && !evaluation?.end_date">
              <span>
                {{
                  getDifference(
                    evaluation.start_date,
                    new Date().toISOString(),
                    false
                  ).text
                }}
              </span>
            </template>

            <template v-if="evaluation.start_date && evaluation?.end_date">
              <span>
                {{
                  getDifference(
                    evaluation.start_date,
                    evaluation?.end_date,
                    false
                  ).text
                }}
              </span>
            </template>
          </div>
        </div>

        <div class="item">
          <div class="title">
            {{ t("common.labels.progress") }}
          </div>
          <div class="data">{{ evaluation.analysis?.progress ?? 0 }}%</div>
        </div>

        <div class="item">
          <div class="title">
            {{ t("common.labels.analysis") }}
          </div>
          <div class="line">
            <label> {{ t("common.labels.pending") }}: </label>
            <div class="value">
              {{ evaluation.analysis?.pending }}
            </div>
          </div>
          <div class="line">
            <label> {{ t("common.labels.started") }}: </label>
            <div class="value">
              {{ evaluation.analysis?.started }}
            </div>
          </div>
          <div class="line">
            <label> {{ t("common.labels.concluded") }}: </label>
            <div class="value">
              {{ evaluation.analysis?.concluded }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.card-evaluation {
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  background: var(--white);
  padding-block: 0.75rem;
  padding-inline: 0.75rem;
  border-radius: 0.75rem;
  gap: 1.25rem;
  border: 2px solid transparent;

  &:hover {
    border-color: var(--primary);
  }

  &.active {
    border-color: var(--primary);
  }

  &.no-progress {
    .header .infos .code {
      width: 7rem;
    }
  }

  .header {
    width: inherit;
    display: flex;
    flex-direction: row;
    gap: 1.25rem;

    .infos {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 0.35rem;

      .content {
        display: flex;
        flex-direction: row;
        align-items: space-between;
        gap: 0.5rem;
      }

      .code {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        width: 9rem;
        gap: 1rem;
        background: var(--white-off);
        border-radius: 0.5rem;
        padding-inline: 0.5rem;
        padding-block: 0.25rem;
        font-size: 1rem;
        font-weight: bold;
      }

      .company {
        max-width: 10rem;
        padding-inline: 0.5rem;
        font-size: 1rem;
      }

      .address {
        padding-inline: 0.5rem;
        font-size: 0.75rem;
      }
    }

    .arrow {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      background: var(--white-off);
      border-radius: 50%;
    }
  }

  .progress {
    display: flex;
    flex-direction: row;
    gap: 3rem;
    flex: 2;
    margin-inline-start: 2rem;

    .item {
      display: flex;
      flex-direction: column;
      gap: 0.15rem;

      .title {
        font-size: 0.9rem;
        text-transform: uppercase;
      }

      .data {
        font-size: 1.25rem;
        font-weight: bold;
        padding-inline-start: 0.25rem;
        padding-block-start: 1rem;
      }

      .time {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.5rem;
        padding-block-start: 0.875rem;
        font-size: 1.25rem;
        font-weight: bold;
      }
    }

    .line {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 0.25rem;
      padding-inline-start: 0.25rem;

      label {
        font-size: 0.85rem;
      }

      .value {
        font-size: 0.875rem;
        font-weight: bold;
      }
    }
  }
}

@media screen and (max-width: 850px) {
  .card-evaluation {
    flex-direction: column;

    .header {
      align-items: center;

      .avatar {
        width: 4rem;
        height: 4rem;
      }
    }

    .progress {
      margin-inline-start: 0;
      gap: 1.5rem;

      .item {
        gap: 0.05rem;

        .title {
          font-size: 0.75rem;
        }

        .data {
          font-size: 1rem;
          padding-block-start: 0.875rem;
        }

        .time {
          font-size: 1rem;
          gap: 0.25rem;

          .svgicon {
            width: 1.25rem;
            height: 1.25rem;
          }
        }
      }

      .line {
        label {
          font-size: 0.875rem;
        }

        .value {
          font-size: 0.875rem;
        }
      }
    }
  }
}
</style>
